<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
    <div
      class="max-w-7xl mx-auto space-y-4 relative pt-2 pb-12 bg-white shadow-md px-2 rounded-sm"
    >
      <!-- Header with Alert Toggle -->
      <div
        class="text-center items-center py-1 text-white bg-pale-sky rounded-lg shadow-md mx-0"
      >
        <h2 class="uppercase text-lg font-bold">
          {{ $t("agent.dashboard.title") }}
        </h2>
      </div>
      <div class="flex justify-between items-center mb-6">
        <div>
          <p class="text-gray-600 text-sm font-medium">
            {{ $t("agent.dashboard.real_time_monitoring") }} •
            {{ $t("agent.dashboard.updated_every_minute") }}
          </p>
        </div>

        <div class="flex items-center space-x-4">
          <div :key="refreshInterval" class="flex items-center space-x-2">
            <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span class="text-sm text-gray-500">Live</span>
            <span class="text-xs text-gray-500 font-bold">
              {{ formatTimestamp(lastUpdated) }}
            </span>
          </div>

          <!-- Alert Toggle Button -->
          <button
            @click="toggleAlerts"
            :class="[
              'flex items-center space-x-2 p-2 rounded-full transition-colors font-medium',
              alerts.length > 0
                ? 'bg-red-200 text-red-700 hover:bg-red-300 hover:text-red-800'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-800',
            ]"
          >
            <!-- <span>🔔</span> -->
            <font-awesome-icon
              :icon="['far', 'bell']"
              :class="[alerts.length > 0 ? 'fa-shake' : '', 'h-4 w-4 ']"
              aria-hidden="true"
              style="--fa-animation-duration: 2s"
            />
            <!-- <span>Alerts ({{ alerts.length }})</span> -->
          </button>
        </div>
      </div>

      <!-- Alert Panel -->
      <div v-if="showAlerts && alerts.length > 0" class="space-y-3 mb-6">
        <div
          v-for="alert in alerts"
          :key="alert.id"
          :class="[
            'p-4 rounded-lg shadow-sm',
            getAlertClass(alert.type, alert.severity),
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 mt-1 text-lg">
                {{ getAlertIcon(alert.type) }}
              </div>
              <div>
                <h4 class="font-semibold text-sm">{{ alert.title }}</h4>
                <p class="text-sm mt-1">{{ alert.message }}</p>
                <span
                  v-if="alert.severity === 'high'"
                  class="inline-block mt-2 px-2 py-1 text-xs font-semibold bg-red-200 text-red-800 rounded-full"
                >
                  {{ $t("agent.dashboard.high_priority") }}
                </span>
              </div>
            </div>
            <button
              @click="dismissAlert(alert.id)"
              class="flex-shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Overview -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <!-- Agents Online -->
        <div
          class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                {{ $t("agent.dashboard.agents_online") }}
              </p>
              <p class="text-3xl font-bold text-green-600">
                {{ totalCounts.online }}
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-full">
              <svg
                class="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Agents Away -->
        <div
          class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                {{ $t("agent.dashboard.agents_away") }}
              </p>
              <p class="text-3xl font-bold text-yellow-500">
                {{ totalCounts.away }}
              </p>
            </div>
            <div class="p-3 bg-yellow-100 rounded-full">
              <svg
                class="w-6 h-6 text-yellow-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Agents Offline -->
        <div
          class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                {{ $t("agent.dashboard.agents_unavailable") }}
              </p>
              <p class="text-3xl font-bold text-red-500">
                {{ totalCounts.notAvailable }}
              </p>
            </div>
            <div class="p-3 bg-red-100 rounded-full">
              <svg
                class="w-6 h-6 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Total Agents -->
        <div
          class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                {{ $t("agent.dashboard.total_agents") }}
              </p>
              <p class="text-3xl font-bold text-indigo-600">
                {{ agentData.length }}
              </p>
            </div>
            <div class="p-3 bg-indigo-100 rounded-full">
              <svg
                class="w-6 h-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- Donut Chart -->
        <div class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <h3 class="text-xl font-semibold text-gray-800 mb-4">
            {{ $t("agent.dashboard.agents_online") }}
          </h3>
          <!-- <apexchart
      type="donut"
      height="350"
      :options="donutChartOptions"
      :series="donutSeries" /> -->
          <apexchart
            type="radialBar"
            height="350"
            :options="radialChartOptions"
            :series="radialSeries"
          />
        </div>

        <!-- Radial Chart -->
        <div class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <h3 class="text-xl font-semibold text-gray-800 mb-4">
            {{ $t("agent.dashboard.agent_stats_distribution") }}
          </h3>
          <apexchart
            type="radialBar"
            height="350"
            :options="chartOptions"
            :series="series"
          />
        </div>
      </div>

      <!-- Filters and Table -->
      <div
        class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
      >
        <!-- Filters -->
        <div class="p-6 bg-gray-50 border-b border-gray-200">
          <div class="flex flex-wrap gap-4 items-center justify-between">
            <h2 class="text-xl font-semibold text-gray-800">
              {{ $t("agent.dashboard.agent_details") }}
            </h2>
            <div class="flex flex-wrap gap-4">
              <!-- Search -->
              <div class="relative">
                <input
                  v-model="searchTerm"
                  type="text"
                  placeholder="Search agents..."
                  class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <svg
                  class="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <!-- Status Filter -->
              <select
                v-model="statusFilter"
                class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">{{ $t("agent.dashboard.all_status") }}</option>
                <option value="Available">{{ $t("available") }}</option>
                <option value="Away">{{ $t("agent.status.away") }}</option>
                <option value="Offline">
                  {{ $t("agent.dashboard.offline") }}
                </option>
              </select>

              <!-- Registration Filter -->
              <select
                v-model="registrationFilter"
                class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">
                  {{ $t("agent.dashboard.all_registration") }}
                </option>
                <option value="true">
                  {{ $t("agent.dashboard.connected") }}
                </option>
                <option value="false">
                  {{ $t("agent.dashboard.disconnected") }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-none">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t("agent.dashboard.agent") }}
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t("agent.dashboard.registration") }}
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  @click="sortBy('registeredDuration')"
                >
                  <div class="flex items-center space-x-1">
                    <span>{{
                      $t("agent.dashboard.registration_duration")
                    }}</span>
                    <svg
                      v-if="sort.key === 'registeredDuration' && sort.isDesc"
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <svg
                      v-else
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </div>
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t("agent.dashboard.status") }}
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  @click="sortBy('durationMinutes')"
                >
                  <div class="flex items-center space-x-1">
                    <span>{{ $t("agent.dashboard.duration") }}</span>
                    <svg
                      v-if="sort.key === 'durationMinutes' && !sort.isDesc"
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <svg
                      v-else
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </div>
                </th>
                <!-- <th
         class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Last Updated
        </th> -->
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="agent in filteredAndSortedData"
                :key="agent.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex gap-2 text-center items-center">
                    <div class="text-sm font-medium text-gray-900">
                      {{ agent.id }}
                    </div>
                    <div
                      :class="[
                        agent.registered && agent.status === 'Available'
                          ? 'bg-green-600'
                          : agent.registered && agent.status !== 'Available'
                          ? 'bg-yellow-400'
                          : 'bg-red-600',
                        'w-2 h-2 rounded-full animate-pulse',
                      ]"
                    ></div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="[
                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                      agent.registered && agent.status === 'Available'
                        ? 'bg-green-100 text-green-800'
                        : agent.registered && agent.status !== 'Available'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800',
                    ]"
                  >
                    {{
                      agent.registered
                        ? $t("agent.dashboard.connected")
                        : $t("agent.dashboard.disconnected")
                    }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="[
                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                      agent.registered
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800',
                    ]"
                  >
                    <!-- {{ agent.registered ? "Connected for" : "Disconnected for" }} -->
                    {{ agent.registeredDuration }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <span
                    :class="[
                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                      getStatusClass(agent.status),
                    ]"
                  >
                    {{ $t(agent.status.toLowerCase()) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ agent.duration }}
                </td>
                <!-- <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         {{ formatTimestamp(agent.lastUpdated) }}
        </td> -->
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div
          v-if="filteredAndSortedData.length === 0"
          class="text-center py-12"
        >
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.457.691-6.266 1.823C7.229 15.655 9.583 15 12 15z"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">
            {{ $t("agent.dashboard.no_agents_found") }}
          </h3>
          <p class="mt-1 text-sm text-gray-500">
            {{ $t("agent.dashboard.no_agents_found_hint") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import ApexChart from "vue3-apexcharts";
import { mapGetters } from "vuex/dist/vuex.cjs.js";

const ALERT_THRESHOLDS = {
  LONG_CONNECTED: 600, // 10 hours
  LONG_DISCONNECTED: 960, // 16 hours
  LONG_UNAVAILABLE: 60, // 1 hour
  VERY_LONG_UNAVAILABLE: 120, // 2 hours
  LONG_AWAY: 1440, // 24 hours
};

export default {
  name: "AgentDashboard",
  components: {
    apexchart: ApexChart,
  },
  data() {
    return {
      ALERT_THRESHOLDS,
      agentData: [],
      noDataForPeriod: false,
      searchTerm: "",
      statusFilter: "",
      registrationFilter: "",
      refreshInterval: null,
      sort: {
        key: "registeredDuration", // The default column to sort by
        isDesc: false, // false for ascending, true for descending
      },
      // Alert-related data
      showAlerts: false,
      dismissedAlerts: new Set(),
    };
  },
  computed: {
    ...mapGetters(["hostName", "pbxMap", "pbx3cxAgentConfig"]),

    lastUpdated() {
      return this.agentData[0]?.timestamps?.current;
    },

    agentsOnline() {
      return this.agentData?.filter(
        (agent) =>
          agent.registration_status.pbx_registered &&
          agent.current_status.code === 0
      );
    },

    agentsAway() {
      return this.agentData?.filter(
        (agent) =>
          agent.registration_status.pbx_registered &&
          agent.current_status.code === 4
      );
    },

    agentsConnectedUnavailable() {
      return this.agentData?.filter(
        (agent) =>
          agent.registration_status.pbx_registered &&
          agent.current_status.code !== 0
      );
    },

    agentsNotAvailable() {
      return this.agentData?.filter(
        (agent) =>
          !agent.registration_status.pbx_registered ||
          agent.current_status.code !== 0
      );
    },

    agentsDisconnected() {
      return this.agentData?.filter(
        (agent) => !agent.registration_status.pbx_registered
      );
    },

    agentsConnected() {
      return this.agentData?.filter(
        (agent) => agent.registration_status.pbx_registered
      );
    },

    totalCounts() {
      return {
        online: this.agentsOnline.length,
        away: this.agentsAway.length,
        connectedUnavailable: this.agentsConnectedUnavailable.length,
        notAvailable: this.agentsNotAvailable.length,
      };
    },

    onlineAvailableCounts() {
      return {
        online: this.agentsConnected?.length
          ? ((this.agentsOnline.length / this.agentData.length) * 100).toFixed(
              0
            )
          : 0,
      };
    },

    // New alerts computed property
    alerts() {
      const alertList = [];

      this.agentData.forEach((agent) => {
        const agentId = agent.user_dn;
        const agentName = this.pbxMap?.extensions[agentId] || "Unknown";
        const statusMinutes = parseInt(agent.status_duration.minutes);
        const registrationMinutes = parseInt(
          agent.registration_status.duration.minutes
        );
        const isRegistered = agent.registration_status.pbx_registered;
        const statusName = agent.current_status.name;

        // Long connected alert
        if (
          isRegistered &&
          registrationMinutes > this.ALERT_THRESHOLDS.LONG_CONNECTED
        ) {
          alertList.push({
            id: `connect-${agentId}`,
            type: "warning",
            title: this.$t("agent.long_connection"),
            message: this.$t("agent.long_connection_detail", {
              agentId: agentId,
              agentName: agentName,
              duration: agent.registration_status.duration.formatted,
            }),
            agent: agentId,
            severity: "medium",
          });
        }

        // Long disconnection alert
        if (
          !isRegistered &&
          registrationMinutes > this.ALERT_THRESHOLDS.LONG_DISCONNECTED
        ) {
          alertList.push({
            id: `disconnect-${agentId}`,
            type: "error",
            title: this.$t("agent.long_disconnection"),
            message: this.$t("agent.long_disconnection_detail", {
              agentId: agentId,
              agentName: agentName,
              duration: agent.registration_status.duration.formatted,
            }),
            agent: agentId,
            severity:
              registrationMinutes > this.ALERT_THRESHOLDS.VERY_LONG_UNAVAILABLE
                ? "high"
                : "medium",
          });
        }

        // Long unavailable alert (Away, Do not disturb, etc.)
        if (
          isRegistered &&
          agent.current_status.code !== 0 &&
          statusMinutes > this.ALERT_THRESHOLDS.LONG_UNAVAILABLE
        ) {
          const severity =
            statusMinutes > this.ALERT_THRESHOLDS.VERY_LONG_UNAVAILABLE
              ? "high"
              : "medium";
          alertList.push({
            id: `unavailable-${agentId}`,
            type: "warning",
            title: this.$t("agent.long_unavailable"),
            message: this.$t("agent.long_unavailable_detail", {
              agentId: agentId,
              agentName: agentName,
              status: statusName.toLowerCase(),
              duration: agent.status_duration.formatted,
            }),
            agent: agentId,
            severity,
          });
        }

        // Very long away alert (specific for Away status)
        if (
          agent.current_status.code === 4 &&
          statusMinutes > this.ALERT_THRESHOLDS.LONG_AWAY
        ) {
          alertList.push({
            id: `long-away-${agentId}`,
            type: "warning",
            title: this.$t("agent.extended_away"),
            message: this.$t("agent.extended_away_detail", {
              agentId: agentId,
              agentName: agentName,
              duration: agent.status_duration.formatted,
            }),
            agent: agentId,
            severity: "medium",
          });
        }
      });

      return alertList.filter((alert) => !this.dismissedAlerts.has(alert.id));
    },

    tableData() {
      console.log("this.pbx3cxAgentConfig ==> ", this.pbx3cxAgentConfig);
      console.log("this.agentData ==> ", this.agentData);
      return this.agentData?.map((agent) => ({
        id:
          agent?.user_dn +
          " - " +
          (this.pbxMap?.extensions[agent?.user_dn] || "Unknown"),
        status:
          agent.current_status.code == 1 && this.pbx3cxAgentConfig
            ? this.pbx3cxAgentConfig?.status_custom1
            : agent.current_status.code == 2 && this.pbx3cxAgentConfig
            ? this.pbx3cxAgentConfig?.status_custom2
            : agent.current_status.name,
        registered: agent.registration_status.pbx_registered,
        registeredDuration: this.formatDuration(
          agent.registration_status.duration.minutes
        ),
        duration:
          agent.status_duration.minutes >= 1440
            ? this.formatDuration(1439)
            : this.formatDuration(agent.status_duration.minutes),
        durationMinutes:
          agent.status_duration.minutes >= 1440
            ? 1439
            : parseInt(agent.status_duration.minutes),
        lastUpdated: agent.timestamps.current,
        user_dn: agent.user_dn, // Add this for alert filtering
      }));
    },

    filteredAndSortedData() {
      let filtered = this.tableData || [];

      // Apply search filter
      if (this.searchTerm) {
        filtered = filtered.filter((agent) =>
          agent.id.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      }

      // Apply status filter
      if (this.statusFilter) {
        if (this.statusFilter === "Offline") {
          filtered = filtered.filter((agent) => !agent.registered);
        } else {
          filtered = filtered.filter(
            (agent) => agent.status === this.statusFilter
          );
        }
      }

      // Apply registration filter
      if (this.registrationFilter) {
        const isRegistered = this.registrationFilter === "true";
        filtered = filtered.filter(
          (agent) => agent.registered === isRegistered
        );
      }

      // Sort by duration
      return filtered.sort((a, b) => {
        const key = this.sort.key;

        const modifier = this.sort.isDesc ? -1 : 1;

        if (a[key] < b[key]) {
          return -1 * modifier;
        }
        if (a[key] > b[key]) {
          return 1 * modifier;
        }
        return 0;
      });
    },

    series() {
      const { online, connectedUnavailable } = this.totalCounts;
      const total = online + connectedUnavailable || 1;
      return [
        Math.round((online / total) * 100),
        // Math.round((away / total) * 100),
        Math.round((connectedUnavailable / total) * 100),
      ];
    },

    radialSeries() {
      const { online } = this.onlineAvailableCounts;
      return [online];
    },

    chartOptions() {
      return {
        chart: {
          type: "radialBar",
          fontFamily: "Inter, sans-serif",
        },
        labels: [this.$t("available"), this.$t("unavailable")],
        colors: ["#10B981", "#EF4444"],
        plotOptions: {
          radialBar: {
            size: undefined,
            inverseOrder: false,
            hollow: {
              margin: 5,
              size: "48%",
              background: "transparent",
            },
            track: {
              show: true,
              startAngle: undefined,
              endAngle: undefined,
              background: "#f1f5f9",
              strokeWidth: "97%",
              opacity: 1,
              margin: 5,
            },
            dataLabels: {
              name: {
                fontSize: "16px",
                color: "#374151",
                offsetY: 0,
              },
              value: {
                fontSize: "14px",
                color: "#6B7280",
                offsetY: 5,
                formatter: (val) => `${val}%`,
              },
              total: {
                show: true,
                label: this.$t("agent.dashboard.agents_connected"),
                fontSize: "14px",
                color: "#6B7280",
                formatter: () => this.agentsConnected.length.toString(),
              },
            },
          },
        },
        legend: {
          show: true,
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "14px",
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          itemMargin: {
            horizontal: 10,
            vertical: 5,
          },
        },
      };
    },

    radialChartOptions() {
      return {
        chart: {
          type: "radialBar",
          fontFamily: "Inter, sans-serif",
        },
        labels: [this.$t("online"), "Away", "Offline"],
        colors: ["#10B981", "#F59E0B", "#EF4444"],
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            dataLabels: {
              name: { fontSize: "14px", color: "#6B7280", offsetY: 0 },
              value: {
                fontSize: "14px",
                color: "#6B7280",
                offsetY: 5,
                formatter: (val) => `${val}%`,
              },
            },
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "dark",
            shadeIntensity: 0.15,
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 50, 65, 91],
          },
        },
        stroke: {
          dashArray: 4,
        },
        legend: {
          show: false,
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "14px",
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          itemMargin: {
            horizontal: 10,
            vertical: 5,
          },
        },
      };
    },
  },

  methods: {
    async getLiveUserStatus() {
      if (this.hostName) {
        let url = `${localStorage.getItem("apiUrl")}/api/v1.2/agent-stats/${
          this.hostName
        }/realtime-status`;
        try {
          const res = await axios.get(url, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("apiToken"),
            },
          });
          console.log("res.data ==> ", res.data);
          this.agentData =
            res.data?.data?.length > 0 ? res.data?.data : this.agentData;
        } catch (error) {
          this.errorHandling(error);
        }
      }
    },

    formatDuration(minutes) {
      const totalMinutes = parseInt(minutes);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      return `${h ? `${h}h ` : ""}${m}m`;
    },

    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleTimeString();
    },

    getStatusClass(status) {
      switch (status) {
        case "Available":
          return "bg-green-100 text-green-800";
        case "Away":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-red-100 text-red-800";
      }
    },

    sortBy(key) {
      if (this.sort.key === key) {
        this.sort.isDesc = !this.sort.isDesc;
      } else {
        this.sort.key = key;
        this.sort.isDesc = false;
      }
    },

    // New alert methods
    dismissAlert(alertId) {
      this.dismissedAlerts.add(alertId);
      this.$forceUpdate(); // Force reactivity update
    },

    toggleAlerts() {
      this.showAlerts = !this.showAlerts;
    },

    getAlertClass(type, severity) {
      if (type === "error" || severity === "high") {
        return "border-red-200 bg-red-50 text-red-800 border-l-4 border-l-red-500";
      }
      return "border-yellow-200 bg-yellow-50 text-yellow-800 border-l-4 border-l-yellow-500";
    },

    getAlertIcon(type) {
      if (type === "error") return "❌";
      if (type === "warning") return "⚠️";
      return "🔔";
    },

    getAgentAlerts(agentDn) {
      return this.alerts.filter((alert) => alert.agent === agentDn);
    },

    startRealTimeUpdates() {
      // Initial load
      this.getLiveUserStatus();

      // Set up interval for real-time updates (every minute)
      this.refreshInterval = setInterval(() => {
        this.getLiveUserStatus();
      }, 20000); // 20 seconds
    },

    stopRealTimeUpdates() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
  },

  mounted() {
    this.startRealTimeUpdates();
  },

  beforeUnmount() {
    this.stopRealTimeUpdates();
  },
};
</script>

<style scoped>
/* Custom styles for enhanced UI */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Smooth transitions */
.transition-colors {
  transition-property: color, background-color, border-color,
    text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.transition-shadow {
  transition-property: box-shadow;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Focus styles */
input:focus,
select:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}
</style>
